<template>
  <div class="pg-wm">
    <!-- 섹션 레벨: 제목 -->
    <div class="p-field">
      <label class="p-field__label">Section Title</label>
      <input
        v-model="sectionData.content.sectionTitle"
        type="text"
        class="p-input"
        placeholder="섹션 제목"
      />
    </div>

    <!-- 문항 탭 -->
    <question-tabs
      :count="questions.length"
      v-model="currentIndex"
      @add="addQuestion"
      @remove="removeQuestion"
    />

    <template v-if="current">
      <div class="p-field">
        <label class="p-field__label">Question / 지시문</label>
        <input
          v-model="current.questionText"
          type="text"
          class="p-input"
          placeholder="예: 표기 옆 빈칸에 알맞은 카드를 드래그하세요"
        />
      </div>

      <!-- 매칭 쌍 -->
      <div class="pg-wm__list">
        <div
          v-for="(pair, index) in current.pairs"
          :key="index"
          class="pg-wm__item"
        >
          <span class="pg-wm__idx">{{ index + 1 }}</span>
          <div class="p-field pg-wm__field">
            <label class="p-field__label">Prompt (고정 표기)</label>
            <input
              v-model="pair.prompt"
              type="text"
              class="p-input"
              placeholder="예: 오백삼십"
            />
          </div>
          <i class="material-icons-round pg-wm__arrow">arrow_forward</i>
          <div class="p-field pg-wm__field">
            <label class="p-field__label">Answer (드래그 카드 · 정답)</label>
            <input
              v-model="pair.answer"
              type="text"
              class="p-input"
              placeholder="예: 530"
            />
          </div>
          <button
            type="button"
            class="pg-wm__icon-btn pg-wm__icon-btn--danger"
            title="제거"
            @click.prevent="removePair(index)"
          >
            <i class="material-icons-round">delete</i>
          </button>
        </div>
      </div>

      <div class="pg-wm__actions">
        <ABtn kind="yellow" icon="add" @click.prevent="addPair">쌍 추가</ABtn>
        <ABtn kind="ghost" icon="upload_file" @click.prevent="triggerExcelUpload">Excel 일괄 등록</ABtn>
        <input
          ref="excelFileInput"
          type="file"
          accept=".xlsx, .xls"
          class="pg-wm__file"
          @change="handleExcelUpload"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref } from "vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import QuestionTabs from "@/components/Practice/QuestionTabs.vue";
import { useQuestions } from "@/components/Practice/useQuestions";
import * as XLSX from "xlsx";

const sectionData = defineModel({ type: Object, required: true });
const excelFileInput = ref(null);

const makeEmpty = () => ({
  questionText: "",
  pairs: [{ prompt: "", answer: "" }]
});

const { questions, currentIndex, current, addQuestion, removeQuestion } = useQuestions(
  sectionData,
  makeEmpty,
  ["questionText", "pairs"]
);

const addPair = () => {
  current.value.pairs.push({ prompt: "", answer: "" });
};

const removePair = (index) => {
  current.value.pairs.splice(index, 1);
};

const triggerExcelUpload = () => {
  excelFileInput.value.click();
};

const handleExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const buf = await file.arrayBuffer();
    const workbook = XLSX.read(buf);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    let added = 0;
    rows.forEach((row) => {
      if (!row || row.length === 0) return;
      const prompt = row[0] != null ? String(row[0]).trim() : "";
      const answer = row[1] != null ? String(row[1]).trim() : "";
      if (prompt || answer) {
        current.value.pairs.push({ prompt, answer });
        added++;
      }
    });
    alert(`${added}개의 쌍을 현재 문제에 추가했습니다.`);
    event.target.value = "";
  } catch (error) {
    console.error("Excel 파일 처리 오류:", error);
    alert("Excel 파일을 읽는 중 오류가 발생했습니다. 파일 형식을 확인해주세요.");
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-wm {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;

    &:hover { border-color: $p-line-hard; }

    @media (max-width: 768px) {
      flex-wrap: wrap;
      align-items: stretch;
    }
  }

  &__idx {
    min-width: 24px;
    height: 24px;
    margin-bottom: 8px;
    display: inline-grid;
    place-items: center;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__field {
    flex: 1;
    min-width: 0;
  }

  &__arrow {
    font-size: 18px !important;
    color: $p-ink-soft;
    margin-bottom: 8px;
    flex-shrink: 0;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    margin-bottom: 4px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;
    flex-shrink: 0;

    > i { font-size: 16px; }

    &--danger {
      color: $p-err;
      &:hover { background: $p-err-soft; }
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__file {
    display: none;
  }
}
</style>
